/**
 * Sweep Control (R27) — pure mapping/display logic. No React, no Supabase: this
 * module backs the node-only vitest suite directly.
 */

import type { RawTriggerResult, TriggerPhase } from "./types";

/** Mirror of the backend INGEST_ERROR_BODY_LIMIT: the ingest error body is hard
 *  sliced to this many chars server-side with no marker, so a stored body at (or
 *  beyond) this length was truncated. */
export const INGEST_ERROR_BODY_LIMIT = 4096;

/** Render the backend's classification. acceptable → Acceptable, unacceptable →
 *  Needs attention, skipped → Skipped; any unrecognised token renders raw rather
 *  than being forced into a label. */
export function classificationLabel(classification: string): string {
  switch (classification) {
    case "acceptable":
      return "Acceptable";
    case "unacceptable":
      return "Needs attention";
    case "skipped":
      return "Skipped";
    default:
      return classification || "Unknown";
  }
}

/** Dispatched vs already-running, from the trigger result's status. */
export function triggerPhase(result: RawTriggerResult): TriggerPhase {
  return result.status === "started" ? "dispatched" : "in_progress";
}

/** Coerce an ingest response body (string | object | null) to a display string,
 *  verbatim for strings; objects are pretty-printed, never summarized. */
export function errorBodyText(body: unknown): string {
  if (body == null) return "";
  if (typeof body === "string") return body;
  try {
    return JSON.stringify(body, null, 2);
  } catch {
    return String(body);
  }
}

/** True when a string body reached the backend's hard 4,096-char slice, so a
 *  truncation marker should be shown. */
export function isErrorBodyTruncated(body: unknown): boolean {
  return typeof body === "string" && body.length >= INGEST_ERROR_BODY_LIMIT;
}
